"use client";

import { RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { Button, cn, HStack } from "@/ui";

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.5;
const PAN_STEP_PX = 40;
const PAN_KEY_DELTAS: Record<string, { x: number; y: number }> = {
  ArrowDown: { x: 0, y: -PAN_STEP_PX },
  ArrowLeft: { x: PAN_STEP_PX, y: 0 },
  ArrowRight: { x: -PAN_STEP_PX, y: 0 },
  ArrowUp: { x: 0, y: PAN_STEP_PX },
};

interface TryOnPreviewZoomProps {
  src: string;
  alt: string;
  className?: string;
}

export function TryOnPreviewZoom(props: TryOnPreviewZoomProps) {
  const { src, alt, className } = props;
  const accessibilityT = useTranslations("accessibility");
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [src]);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + SCALE_STEP, MAX_SCALE));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => {
      const newScale = Math.max(prev - SCALE_STEP, MIN_SCALE);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (scale > 1) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      }
    },
    [scale, position],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (isDragging && scale > 1) {
        e.preventDefault();
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, scale, dragStart],
  );

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handlePanKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (scale <= 1) return;
      const delta = PAN_KEY_DELTAS[e.key];
      if (!delta) return;
      e.preventDefault();
      setPosition((current) => ({ x: current.x + delta.x, y: current.y + delta.y }));
    },
    [scale],
  );

  const handleDoubleClick = useCallback(() => {
    if (scale > 1) handleReset();
    else setScale(2);
  }, [scale, handleReset]);

  return (
    <div className={cn("flex w-full max-w-md flex-col gap-2 self-center", className)}>
      <HStack className="justify-end gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8 rounded-sm"
          onClick={handleZoomIn}
          disabled={scale >= MAX_SCALE}
          aria-label={accessibilityT("zoom_in")}
        >
          <ZoomIn className="size-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8 rounded-sm"
          onClick={handleZoomOut}
          disabled={scale <= MIN_SCALE}
          aria-label={accessibilityT("zoom_out")}
        >
          <ZoomOut className="size-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8 rounded-sm"
          onClick={handleReset}
          disabled={scale === 1 && position.x === 0 && position.y === 0}
          aria-label={accessibilityT("reset")}
        >
          <RotateCcw className="size-4" />
        </Button>
      </HStack>

      <div
        className="relative aspect-3/4 w-full overflow-hidden rounded-sm border bg-muted/20"
        tabIndex={scale > 1 ? 0 : undefined}
        aria-label={scale > 1 ? alt : undefined}
        onKeyDown={handlePanKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div
          className={cn(
            "flex size-full items-center justify-center transition-transform duration-200 ease-out",
            isDragging ? "cursor-grabbing" : scale > 1 ? "cursor-grab" : "cursor-zoom-in",
          )}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          }}
          onDoubleClick={handleDoubleClick}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={alt}
            className="max-h-full max-w-full object-contain"
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
}
